import React, { useCallback, useState, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Flex, Box, Text, Pressable } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useCreateContentFromTemplate } from '@confluence/create-content-from-template/entry-points/useCreateContentFromTemplate.ts';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';

import { PensIcon } from './PensIcon.tsx';
import { useNthUserOnboardingBanner } from './useNthUserOnboardingBanner.tsx';
import { Team } from './teamTypes.ts';

const roundedBoxStyles = cssMap({
	root: {
		borderRadius: token('radius.large'),
		borderColor: token('color.border.information'),
		boxShadow: token('elevation.shadow.raised'),
		width: '100%',
		maxWidth: '770px',
		padding: token('space.100'),
	},
});

const observerStyles = cssMap({
	root: {
		height: '1px',
		width: '100%',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

const outerContainerStyles = cssMap({
	root: {
		display: 'flex',
		justifyContent: 'center',
	},
});

const innerContainerStyles = cssMap({
	locked: {
		position: 'relative',
		pointerEvents: 'auto',
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		marginBottom: token('space.200'),
		marginTop: token('space.200'),
	},
	floating: {
		position: 'fixed',
		bottom: token('space.200'),
		pointerEvents: 'auto',
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		marginBottom: token('space.100'),
	},
});

const linkPressableStyles = cssMap({
	root: {
		backgroundColor: token('color.background.information'),
		border: 'none',
		padding: token('space.0'),
		cursor: 'pointer',
		'&:hover': {
			textDecoration: 'underline',
			color: token('color.link'),
		},
	},
});

const i18n = defineMessages({
	nthUserOnboardingBannerTitle: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.title',
		defaultMessage: 'Next up: start creating',
		description: 'Title of the Nth User Onboarding Banner',
	},
	nthUserOnboardingBannerAction: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.action',
		defaultMessage: 'Try a {blueprintName} template',
		description: 'Action of the Nth User Onboarding Banner',
	},
	nthUserOnboardingBannerActionFallback: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.action-fallback',
		defaultMessage: 'Create a page',
		description: 'Fallback action of the Nth User Onboarding Banner when no template is available',
	},
	nthUserOnboardingBannerDismiss: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button of the Nth User Onboarding Banner',
	},
});

const DELAY_TIMEOUT = 8000;

const NthUserOnboardingBannerComponentInternal = ({
	contentID,
	spaceKey,
}: {
	contentID: string;
	spaceKey: string | undefined;
}) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isLocked, setIsLocked] = useState(false);
	const observerRef = useRef<HTMLDivElement>(null);
	const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
	const isLockedRef = useRef(false);
	const hasBeenTriggered = useRef(false);

	const { setOnboardingState } = useSetOnboardingState();
	const createContentFromTemplate = useCreateContentFromTemplate();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const { isUserEligible, isLoading, blueprintName, teamType, templateToCreate } =
		useNthUserOnboardingBanner({
			contentID,
		});

	// Show banner after delay
	useEffect(() => {
		if (!isUserEligible) return;

		isLockedRef.current = isLocked;

		const delayTimeout = setTimeout(() => {
			setIsVisible(true);
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'viewNthUserOnboardingBanner',
				},
			}).fire();
		}, DELAY_TIMEOUT);

		return () => clearTimeout(delayTimeout);
	}, [isUserEligible, isLocked, createAnalyticsEvent]);

	// Intersection observer for banner positioning
	useEffect(() => {
		if (!isVisible || !isUserEligible) return;

		const targetElement = observerRef.current;
		if (!targetElement) return;

		let pollInterval: number | null = null;

		const checkPosition = () => {
			const targetRect = targetElement.getBoundingClientRect();
			const isTargetWellBelowViewport = targetRect.top > window.innerHeight + 100;
			if (isTargetWellBelowViewport && isLockedRef.current) {
				setIsLocked(false);
				hasBeenTriggered.current = false;
				if (pollInterval) {
					clearInterval(pollInterval);
					pollInterval = null;
				}
			}
		};

		const handleIntersection = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !hasBeenTriggered.current) {
					setIsLocked(true);
					hasBeenTriggered.current = true;
					pollInterval = setInterval(checkPosition, 100);
				}
			});
		};

		intersectionObserverRef.current = new IntersectionObserver(handleIntersection, {
			root: null,
			rootMargin: '0px',
			threshold: 0,
		});
		intersectionObserverRef.current.observe(targetElement);

		return () => {
			if (intersectionObserverRef.current) {
				intersectionObserverRef.current.disconnect();
			}
			if (pollInterval) {
				clearInterval(pollInterval);
			}
		};
	}, [isVisible, isUserEligible]);

	const handleDismiss = useCallback(() => {
		setIsVisible(false);
		setOnboardingState({
			key: ONBOARDING_EXPERIENCES.NTH_USER_BANNER_DISMISSED,
			value: 'true',
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'nthUserGSPage',
				action: 'dismissed',
				actionSubject: 'banner',
				actionSubjectId: 'nthUserOnboardingBanner',
			},
		}).fire();
	}, [setOnboardingState, createAnalyticsEvent]);

	const handleTemplateClick = useCallback(() => {
		try {
			if (spaceKey) {
				if (templateToCreate) {
					createContentFromTemplate(templateToCreate, spaceKey, {
						openContentInNewTab: true,
					});
				} else if (teamType === Team.SoftwareDevelopment) {
					// If the user is in a software development team, we need to create a flowchart template - separate flow
					const softwareTemplateCreationLink = buildCreationLink({
						contentType: 'whiteboard',
						source: 'nthUserOnboardingBanner',
						spaceKey,
						insertTemplateWithKey: 'flow-chart',
						openTemplatePicker: false,
					});
					window.open(softwareTemplateCreationLink, '_blank');
				} else {
					// default to create a blank page - no team type or template available
					const createUrl = buildCreationLink({
						contentType: 'page',
						source: 'nthUserOnboardingBanner',
						spaceKey,
					});
					window.open(createUrl, '_blank');
				}

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'nthUserGSPage',
						action: 'clicked',
						actionSubject: 'banner',
						actionSubjectId: 'nthUserOnboardingBanner',
					},
				}).fire();
			}
			handleDismiss();
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'nthUserGSPage',
					action: 'failed',
					actionSubject: 'template creation',
					actionSubjectId: 'nthUserOnboardingBanner',
					attributes: {
						error: error?.message || error || 'Unknown error',
					},
				},
			}).fire();
			handleDismiss();
		}
	}, [
		createContentFromTemplate,
		createAnalyticsEvent,
		handleDismiss,
		spaceKey,
		templateToCreate,
		teamType,
	]);

	if (!isVisible || !isUserEligible || isLoading) {
		return null;
	}

	return (
		<>
			<Box ref={observerRef} xcss={observerStyles['root']} />
			<Box xcss={outerContainerStyles['root']} data-vc="page-layout-nth-user-onboarding-banner">
				<Box xcss={innerContainerStyles[isLocked ? 'locked' : 'floating']}>
					<Box xcss={roundedBoxStyles['root']} backgroundColor="color.background.information">
						<Flex justifyContent="space-between" alignItems="center">
							<Flex alignItems="center" gap="space.200">
								<Box>
									<PensIcon size={50} />
								</Box>
								<Text weight="bold">
									<FormattedMessage {...i18n.nthUserOnboardingBannerTitle} />
								</Text>
							</Flex>
							<Flex alignItems="center" gap="space.200">
								<Pressable onClick={handleTemplateClick} xcss={linkPressableStyles['root']}>
									<Text color="color.link">
										{blueprintName ? (
											<FormattedMessage
												{...i18n.nthUserOnboardingBannerAction}
												values={{ blueprintName }}
											/>
										) : (
											<FormattedMessage {...i18n.nthUserOnboardingBannerActionFallback} />
										)}
									</Text>
								</Pressable>
								<IconButton
									onClick={handleDismiss}
									appearance="subtle"
									icon={(iconProps) => <CrossIcon {...iconProps} />}
									label={formatMessage(i18n.nthUserOnboardingBannerDismiss)}
									isTooltipDisabled={false}
								/>
							</Flex>
						</Flex>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export const NthUserOnboardingBannerComponent = (props: {
	contentID: string;
	spaceKey: string | undefined;
}) => (
	<ErrorBoundary attribution={Attribution.FLYWHEEL_ENGAGEMENT}>
		<NthUserOnboardingBannerComponentInternal {...props} />
	</ErrorBoundary>
);
